import { useEffect, useMemo, useState } from "react";
import { countryOption, normalizeCountryLower } from "../lib/saints/countries";

function flagCdnSvgUrl(iso2) {
  const cc = String(iso2 || "").toLowerCase();
  if (!/^[a-z]{2}$/.test(cc)) return "";
  return `https://flagcdn.com/${cc}.svg`;
}

function GlobeIcon({ className = "h-4 w-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c3 3 3 15 0 18" />
      <path d="M12 3c-3 3-3 15 0 18" />
    </svg>
  );
}

function UnknownIcon({ className = "h-4 w-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M5 4v16" />
      <path d="M5 5h10l-2 3 2 3H5" />
      <path d="M15.5 14.5c1.5-.8 2.5-1.8 2.5-3.2 0-2-1.6-3.3-3.7-3.3-1.6 0-2.9.7-3.6 2" />
      <path d="M14.3 18.2h.01" />
    </svg>
  );
}

export default function CountryFlag({
  saint,
  value,
  showName = false,
  className = "",
  imgClassName = "h-4 w-6 rounded-sm border border-black/10 dark:border-white/10 object-cover",
  nameClassName = "",
  title,
}) {
  const countryLower = useMemo(() => {
    if (value) return String(value).toLowerCase();
    if (saint) return normalizeCountryLower(saint);
    return "";
  }, [value, saint]);

  const opt = useMemo(() => countryOption(countryLower), [countryLower]);

  const kind = opt?.value;
  const iso2 = opt?.iso2 || null;
  const name = opt?.name || "";

  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    setImgOk(true);
  }, [iso2]);

  if (!opt) return null;

  const wrapperBase =
    "inline-flex items-center gap-2 text-gray-700 dark:text-gray-200";
  const finalNameClass =
    nameClassName || "text-sm text-gray-700 dark:text-gray-200";

  // Specials: no real flag exists
  if (kind === "various") {
    const t = title ?? name;
    return (
      <span className={`${wrapperBase} ${className}`} title={t}>
        <GlobeIcon className="h-4 w-4" />
        {showName ? <span className={finalNameClass}>{name}</span> : null}
      </span>
    );
  }

  if (kind === "unknown" || !iso2) {
    const t = title ?? name;
    return (
      <span className={`${wrapperBase} ${className}`} title={t}>
        <UnknownIcon className="h-4 w-4" />
        {showName ? <span className={finalNameClass}>{name}</span> : null}
      </span>
    );
  }

  const src = flagCdnSvgUrl(iso2);
  const t = title ?? name;

  return (
    <span className={`${wrapperBase} ${className}`} title={t}>
      {imgOk ? (
        <img
          src={src}
          alt={name || "Country flag"}
          className={imgClassName}
          loading="lazy"
          onError={() => setImgOk(false)}
        />
      ) : null}
      {showName ? <span className={finalNameClass}>{name}</span> : null}
    </span>
  );
}
